import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import Button, { buttonTypes } from '../../../common/Button';
import { useTheme } from '../../../../components/Theme/ThemeContext';
import packageJSON from '../../../../../package.json';
import { getString } from '../../../../services/api/store';
import { Flex } from '../../../common/Flex';
import { getLocalAppStateAsync } from '../../../../services/api/db';
import { BUILD_VERSION } from '../../../../config';
import { ContentContainer, TitleText } from '../details';
import { Divider } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { aboutLinks } from '../../../SignInPage/constants/strings';
import ConferenceCompassIcon from '../../../../assets/icons/ConferenceCompassIcon';
import FacebookIcon from '../../../../assets/icons/FacebookIcon';
import TwitterIcon from '../../../../assets/icons/TwitterIcon';
import LinkedinIcon from '../../../../assets/icons/LinkedinIcon';

const VersionDetailsContainer = styled.div`
    width: 100%;
`;

const VersionDetailsText = styled.div`
    font-family: 'Roboto', sans-serif;
    font-size: 15px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.6;
    letter-spacing: normal;
    text-align: left;
    color: rgba(0, 0, 0, 0.87);

    div {
        font-size: 11px;
        font-weight: normal;
        font-stretch: normal;
        font-style: normal;
        line-height: 1.82;
        letter-spacing: normal;
        text-align: left;
        color: rgba(0, 0, 0, 0.6);
    }
`;

const VersionContainer = styled.div`
    align-self: stretch;
    flex-grow: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 16px;
`;

const StyledDivider = styled(Divider)`
    width: calc(100% + 32px);
    margin: 0 -16px;
`;

const LinkItem = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 12px 16px;
    font-family: Roboto;
    font-size: 15px;
    line-height: 1.6;
    color: rgba(0, 0, 0, 0.87);
    border-radius: 8px;
    margin-top: 8px;

    &:hover {
        background-color: rgba(0, 0, 0, 0.04);
    }
`;

const StyledAnchor = styled.a`
    position: relative;
    text-decoration: none;
    width: calc(100% + 32px);
`;

const StyledIconAnchor = styled.a`
    position: relative;
    text-decoration: none;
`;

const FooterText = styled.div`
    font-family: Roboto;
    font-size: 13px;
    line-height: 1.6;
    color: rgba(0, 0, 0, 0.6);
    text-align: center;
    margin-top: ${props => (props.nomargin ? 0 : '8px')};
`;

const IconsContainer = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    align-items: center;
    justify-content: center;
    margin: 24px 0;
`;

const UpdateAvailableLabel = ({ theme, text }) => (
    <Button
        text={text}
        type={buttonTypes.GREEN_REVERSE}
        background={theme.primary}
        onClick={() => {}}
        style={{ height: '24px', borderRadius: '50px', padding: '0 12px' }}
        enabled={false}
    />
);

const PlatformInfoContent = ({
    updateAvailable = false,
    updateServiceWorker = () => {},
    waitingWorker,
}) => {
    const [releaseNum, setReleaseNum] = useState(null);
    const [itemHovered, setItemHovered] = useState(null);

    const { theme } = useTheme();
    const { version: platformVersion } = packageJSON;

    const VERSION_TEXT = getString('appVersionText') || 'Platform version';
    const DATA_TEXT = getString('dataVersionText') || 'Data';
    const UPDATE_TEXT = getString('updateAvailableTitle') || 'Update';

    useEffect(() => {
        (async () => {
            const appState = await getLocalAppStateAsync();
            if (appState.releaseNum) {
                setReleaseNum(appState.releaseNum);
            }
        })();
    }, []);

    return (
        <ContentContainer>
            <StyledDivider variant="fullWidth" style={{ transform: 'translateY(-16px)' }} />
            <VersionContainer>
                <TitleText>Version</TitleText>
                <VersionDetailsContainer>
                    <Flex justifyContent={'space-between'}>
                        <VersionDetailsText>
                            {VERSION_TEXT}: <b>{platformVersion}</b>
                            {BUILD_VERSION && <div>{BUILD_VERSION}</div>}
                        </VersionDetailsText>
                        {updateAvailable && waitingWorker && (
                            <UpdateAvailableLabel theme={theme} text={UPDATE_TEXT} />
                        )}
                    </Flex>
                    {releaseNum && (
                        <Flex justifyContent={'space-between'} margin={'8px 0'}>
                            <VersionDetailsText>
                                {DATA_TEXT}: <b>{releaseNum}</b>
                            </VersionDetailsText>
                            {updateAvailable && (
                                <UpdateAvailableLabel theme={theme} text={UPDATE_TEXT} />
                            )}
                        </Flex>
                    )}
                </VersionDetailsContainer>
                <Flex flexdirection={'column'} justifyContent={'center'} alignItems={'center'}>
                    {updateAvailable && updateServiceWorker && (
                        <Button
                            text={'Update'}
                            type={buttonTypes.GREEN_LONG}
                            background={theme.primary}
                            onClick={updateServiceWorker}
                        />
                    )}
                </Flex>
            </VersionContainer>
            <StyledDivider variant="fullWidth" />
            <StyledAnchor
                href={aboutLinks.termsAndConditions}
                target="_blank"
                onMouseEnter={() => setItemHovered('TOU')}
                onMouseLeave={() => setItemHovered(null)}
            >
                <LinkItem>Terms of Use {itemHovered === 'TOU' && <OpenInNewIcon />}</LinkItem>
            </StyledAnchor>
            <StyledAnchor
                href={aboutLinks.privacyPolicy}
                target="_blank"
                onMouseEnter={() => setItemHovered('PP')}
                onMouseLeave={() => setItemHovered(null)}
            >
                <LinkItem>Privacy Policy {itemHovered === 'PP' && <OpenInNewIcon />}</LinkItem>
            </StyledAnchor>
            <StyledAnchor
                href={aboutLinks.cookiePolicy}
                target="_blank"
                onMouseEnter={() => setItemHovered('CP')}
                onMouseLeave={() => setItemHovered(null)}
            >
                <LinkItem>Cookie Policy {itemHovered === 'CP' && <OpenInNewIcon />}</LinkItem>
            </StyledAnchor>
            <StyledAnchor
                href={aboutLinks.releaseNotes}
                target="_blank"
                onMouseEnter={() => setItemHovered('RN')}
                onMouseLeave={() => setItemHovered(null)}
            >
                <LinkItem>Release Notes {itemHovered === 'RN' && <OpenInNewIcon />}</LinkItem>
            </StyledAnchor>
            <StyledDivider variant="fullWidth" style={{ marginTop: 16, marginBottom: 16 }} />
            <FooterText>
                We are here to help you get the most out of your time at an event, by building
                powerful, engaging and easy-to-use online and mobile event apps.
            </FooterText>
            <IconsContainer>
                <StyledIconAnchor
                    href="https://www.conferencecompass.com/"
                    target="_blank"
                    style={{ marginRight: 16 }}
                >
                    <ConferenceCompassIcon />
                </StyledIconAnchor>
                <StyledIconAnchor
                    href="https://www.facebook.com/ConferenceCompass"
                    target="_blank"
                    style={{ marginRight: 16 }}
                >
                    <FacebookIcon />
                </StyledIconAnchor>
                <StyledIconAnchor
                    href="https://twitter.com/ConfCompass"
                    target="_blank"
                    style={{ marginRight: 16 }}
                >
                    <TwitterIcon />
                </StyledIconAnchor>
                <StyledIconAnchor
                    href="https://www.linkedin.com/company/conferencecompass"
                    target="_blank"
                >
                    <LinkedinIcon />
                </StyledIconAnchor>
            </IconsContainer>
            <FooterText nomargin>Conference Compass</FooterText>
            <FooterText nomargin>Binckhorstlaan 36, Unit M2.18</FooterText>
            <FooterText nomargin>2516 BE, The Hague</FooterText>
        </ContentContainer>
    );
};

export default PlatformInfoContent;
